import { useCallback, useEffect, useState } from 'react';
import { EventType, HarmReport, ReportStatus } from '@inplay/models';
import { reportService } from '../service/instances';
import { HarmReportModel } from '../models/harmReportModel';
import { DeadlineInfo } from './useDashboardHarmReports.types';
import { useWebsocketSubscription } from './useWebsocketSubscription';

const buildDeadlineMap = (reports: HarmReport[]): Map<string, DeadlineInfo> => {
  const map: Map<string, DeadlineInfo> = new Map();

  reports.forEach((report: HarmReport): void => {
    const model: HarmReportModel = new HarmReportModel(report);

    if (!model.hasActiveDeadline) return;

    const playerId: string = model.playerId;
    const expiresAt: Date = model.expiresAt!;
    const deadlineType: DeadlineInfo['deadlineType'] = model.deadlineType!;

    const existing: DeadlineInfo | undefined = map.get(playerId);
    if (existing && existing.expiresAt.getTime() <= expiresAt.getTime()) {
      return;
    }

    map.set(playerId, { expiresAt, deadlineType });
  });

  return map;
};

export const useDashboardHarmReports = (
  venueBranchId: string | undefined,
): { deadlineMap: Map<string, DeadlineInfo>; loading: boolean } => {
  const [deadlineMap, setDeadlineMap] = useState<Map<string, DeadlineInfo>>(
    new Map(),
  );
  const [loading, setLoading] = useState<boolean>(false);

  const fetchHarmReports = useCallback(
    async (fromSocket?: boolean): Promise<void> => {
      if (!venueBranchId) return;

      if (!fromSocket) setLoading(true);
      try {
        const currentDate: Date = new Date();
        const response = await reportService.getHarmReports({
          venueBranchId,
          currentDate,
          startDate: currentDate,
          endDate: currentDate,
          status: [ReportStatus.ACTIVE],
        });
        const harmReports: HarmReport[] = response.data?.harmReports ?? [];
        const map: Map<string, DeadlineInfo> = buildDeadlineMap(harmReports);
        setDeadlineMap(map);
      } catch (error: unknown) {
        console.error(
          '[useDashboardHarmReports] Failed to fetch harm reports:',
          error,
        );
        setDeadlineMap(new Map());
      } finally {
        setLoading(false);
      }
    },
    [venueBranchId],
  );

  useEffect(() => {
    fetchHarmReports();
  }, [fetchHarmReports]);

  useWebsocketSubscription(
    venueBranchId,
    [EventType.HARM, EventType.INTERVENTION],
    () => fetchHarmReports(true),
  );

  return { deadlineMap, loading };
};
